



















































































































































































































































































































import Component from 'vue-class-component';
import {
  DataGridColumn,
  dataGridValidationResultIsError,
  dataGridValidationResultIsRequired,
  IDataGridColumnValidatorResult,
} from './DataGridColumn.class';
import { DataGridColumnType } from './DataGridColumnType.class';
import { debounce } from 'debounce';
import { DataGridOptions } from './DataGridOptions.class';
import distinct from '@/utils/filters/arrayFilterDistinct';
import Checkbox from '@/components/Checkbox.vue';
import { isUndefined } from '@/utils/isUndefined';
import BaseComponent from '../BaseComponent';
import { DataGridErrorType } from '@/typing/enums/dataGridErrorType.enum';
import $dayjs from '@/utils/dayjs';

@Component({
  props: {
    items: {
      default: Array,
      type: Array,
    },
    columns: {
      default: Array,
      type: Array,
    },
    options: {
      type: Object,
      required: false,
    },
  },
  components: {
    Checkbox,
  },
})
export default class DataGrid extends BaseComponent {
  currentlyEditingCell: {
    itemIndex: number;
    columnIndex: number;
  } | null = null;
  columnTypes = DataGridColumnType;
  errors: IDataGridColumnValidatorResult[] = [];
  selectedItems: any[] = [];
  generalSelectionValue = false;
  isUndefined = isUndefined;
  showSelectionBoxes = false;
  allItems: any[] = [];
  selectPossibilities: string[] = [];
  errorTypes = DataGridErrorType;
  setSelectDebounced: any;
  validateItemsDebounced: any;
  dayjs = $dayjs;
  renderedIndexes: number[] = [];
  debounceDetermineRenderedIndexes: any;

  private _emitChangesDebounced: any;

  get gridOptions(): DataGridOptions {
    return this.$props.options;
  }

  get shownColumns(): DataGridColumn[] {
    return this.$props.columns;
  }

  get shownItems(): unknown[] {
    return [
      ...this.allItems.filter(this.gridOptions.shownItemsFilterFunc),
    ].reverse();
  }

  determineRenderedIndexes(): void {
    const gridContainer = document.querySelector('.data-grid-wrapper') as any;
    const offsetTopGrid = gridContainer?.offsetTop ?? 0;
    const scrollPosition = gridContainer?.scrollTop ?? 0;
    const visible = {
      start: scrollPosition - 100,
      end: scrollPosition + gridContainer.offsetHeight + 100,
    };

    const toRenderItems: any[] = [];
    const rows: any[] = Array.from(
      document.querySelectorAll('.f-tr-container')
    );

    rows.some((el: HTMLDivElement) => {
      const itemIndex = el.id.split('-')[1];
      const startPosition = el.offsetTop - offsetTopGrid;
      const endPosition = startPosition + el.offsetHeight;

      if (startPosition >= visible.start) {
        if (endPosition <= visible.end) {
          toRenderItems.push(Number(itemIndex));
        } else {
          toRenderItems.push(Number(itemIndex));
          return true;
        }
      }
    });

    this.renderedIndexes = [...toRenderItems];
  }

  mounted(): void {
    this.debounceDetermineRenderedIndexes = debounce(
      this.determineRenderedIndexes,
      200
    );
    this.validateItemsDebounced = debounce(this.validateEditableItems, 200);
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key == 'Escape') {
        if (this.currentlyEditingCell) {
          Array.from(document.querySelectorAll('input')).forEach((el) =>
            el.blur()
          );
          this.onInputBlur(
            null,
            this.currentlyEditingCell.itemIndex,
            this.currentlyEditingCell.columnIndex
          );
          this.currentlyEditingCell = null;
        }
      }
    });

    this._emitChangesDebounced = debounce(this._emitChanges, 200);
    this.setSelectDebounced = debounce(this.setSelectPossiblities, 200);
    this.allItems = [...this.$props.items];
    this.showSelectionBoxes = this._checkIfSelectionBoxesShouldBeVisible();

    // Horizontal scroll
    (this.$refs['scroll-container'] as any).addEventListener('scroll', () => {
      (this.$refs['header-container'] as any).style.left = `-${
        (this.$refs['scroll-container'] as any).scrollLeft
      }px`;
      this.debounceDetermineRenderedIndexes();
    });

    // Vertical scroll
    document
      .querySelector('.data-grid-wrapper')
      ?.addEventListener('scroll', () => {
        this.debounceDetermineRenderedIndexes();
      });

    this.$watch(
      () => this.shownItems.length,
      (n: number, o: number) => {
        if (n > o) {
          this.$nextTick(this.debounceDetermineRenderedIndexes);
        }
      }
    );

    this.$watch(
      () => this.$props.items,
      () => {
        this.allItems = [...this.$props.items];
      }
    );

    this.$watch(
      () => this.shownItems,
      () =>
        (this.showSelectionBoxes = this._checkIfSelectionBoxesShouldBeVisible())
    );

    this.$watch(
      () => this.currentlyEditingCell?.columnIndex ?? -1,
      (n: number) => {
        if (n === -1) return;
        const column = this.shownColumns[n];
        if (
          column.selectOptions ||
          column.additionalInputType === this.columnTypes.Select
        ) {
          this.setSelectDebounced();
        }
      }
    );

    this.$watch(
      () => this.errors.length,
      () => {
        this.$emit('validationErrors', [...this.errors]);
      }
    );

    this.$nextTick(this.debounceDetermineRenderedIndexes);
  }

  onDatePickerInput(
    val: string,
    itemIndex: number,
    column: DataGridColumn
  ): void {
    if (!column.dateValueKey) return;
    this.allItems[itemIndex][column.dateValueKey] = val;
    this._emitChangesDebounced(itemIndex, this.allItems[itemIndex]);
    this.$emit('dateUpdated', {
      newValue: val,
      item: this.allItems[itemIndex],
      itemIndex,
      columns: this.shownColumns,
      column,
    });
  }

  isCurrentlyEditingCell(itemIndex: number, columnIndex: number): boolean {
    if (!this.currentlyEditingCell) return false;

    return (
      this.currentlyEditingCell.itemIndex === itemIndex &&
      this.currentlyEditingCell.columnIndex === columnIndex
    );
  }

  validateEditableItems(): void {
    this.errors = [];
    this.allItems.forEach((item: any, itemIndex: number) => {
      if (
        this.gridOptions.shownItemsFilterFunc(item) &&
        this.gridOptions.isRowEditableFunc(item)
      ) {
        this.shownColumns.forEach((col: DataGridColumn, colIndex: number) => {
          const validationResult = col.validate(
            item,
            this.allItems,
            this.shownColumns
          );

          if (validationResult.hasError) {
            validationResult.columnIndex = colIndex;
            validationResult.itemIndex = itemIndex;
            this.errors.push(validationResult);
          }
        });

        this.gridOptions.additionalItemValidators.forEach(
          (validatorFn: any) => {
            const result = validatorFn(item);

            if (result.hasError) {
              result.itemIndex = itemIndex;
              result.columnIndex = -1;
              this.errors.push(result);
            }
          }
        );
      }
    });

    this.showSelectionBoxes = this._checkIfSelectionBoxesShouldBeVisible();
  }

  onTabOrEnterEventInput(
    itemIndex: number,
    shownItemIndex: number,
    columnIndex: number,
    tabPressed: boolean
  ): void {
    const value = this._getValueFromCell(itemIndex, columnIndex);
    const column = this.shownColumns[columnIndex];

    if (
      value === null ||
      value === undefined ||
      value.toString().trim() === '' ||
      column.autoComplete
    ) {
      const column = this.shownColumns[columnIndex];
      const item: any = this.allItems[itemIndex];
      const fillerValue = column.getUndefinedValue(
        item,
        this.selectPossibilities
      );

      if (fillerValue.usedSelectionOption) {
        this.$emit('selectedOption', {
          value: fillerValue.value,
          itemRef: item,
        });
      }

      if (fillerValue.value != null) {
        item[column.valueKey] = fillerValue.value;
      }
    }

    if (tabPressed) {
      this.moveCell(itemIndex, columnIndex - 1);
    } else {
      this.moveCell(itemIndex, columnIndex + 1, true);
    }
  }

  onInputFocus(itemIndex: any, columnIndex: number): void {
    if (this.currentlyEditingCell) {
      this.onInputBlur(
        null,
        this.currentlyEditingCell.itemIndex,
        this.currentlyEditingCell.columnIndex
      );
    }
    this.currentlyEditingCell = {
      itemIndex,
      columnIndex,
    };
  }

  onInputKeyUp(e: KeyboardEvent, itemIndex: number): void {
    const char = e.key.toLowerCase();

    if (char === 'tab') {
      this._emitChangesDebounced(itemIndex, this.allItems[itemIndex]);
    }

    this.setSelectDebounced();
  }

  runDateUndefinedForItem(itemIndex: number, dateValueKeys: string[]): void {
    const item = this.allItems[itemIndex];
    this.shownColumns
      .filter((x) => x.dateValueKey && dateValueKeys.includes(x.dateValueKey))
      .forEach((column) => {
        if (
          column.dateValueKey &&
          column.dateUndefined &&
          item[column.dateValueKey]
        ) {
          const val = column.dateUndefined(item, column, this.shownColumns);
          this.allItems[itemIndex][column.dateValueKey] = val;
        }
      });
    this._emitChanges(itemIndex, this.allItems[itemIndex]);
  }

  onInputBlur(event: any | null, itemIndex: number, columnIndex: number): void {
    let isProgrammaticBlur = !event;

    if (!isProgrammaticBlur) {
      if (
        event.explicitOriginalTarget &&
        event.explicitOriginalTarget.parentElement
      ) {
        if (
          event.explicitOriginalTarget.parentElement.className ===
            'select-item' ||
          event.explicitOriginalTarget.className === 'select-item' ||
          event.explicitOriginalTarget.className === 'select-list'
        ) {
          return;
        }
      }
    }

    const column: DataGridColumn = this.shownColumns[columnIndex];
    const item = this.allItems[itemIndex];
    if (
      !!item[column.valueKey] &&
      column.dateValueKey &&
      !item[column.dateValueKey] &&
      column.dateUndefined
    ) {
      this.allItems[itemIndex][column.dateValueKey] = column.dateUndefined(
        item,
        column,
        this.shownColumns
      );
    }

    this.selectPossibilities = [];

    const hasErrorIndex = this.errors.findIndex(
      (error: any) =>
        error.itemIndex === itemIndex && error.columnIndex === columnIndex
    );

    if (hasErrorIndex > -1) {
      this.errors.splice(hasErrorIndex, 1);
    }

    const validationResult = column.validate(
      this.allItems[itemIndex],
      this.allItems,
      this.shownColumns
    );

    this.gridOptions.additionalItemValidators.forEach(
      (validator: (item: any) => IDataGridColumnValidatorResult) => {
        const result = validator(this.allItems[itemIndex]);
        result.columnIndex = -1;
        result.itemIndex = itemIndex;
        this.errors.push(result);
      }
    );

    this.errors = this.errors.filter(
      (error: any) => error.columnIndex !== -1 && error.itemIndex === itemIndex
    );

    if (validationResult.hasError) {
      validationResult.itemIndex = itemIndex;
      validationResult.columnIndex = columnIndex;
      const previousColumnErrorIndex = this.errors.findIndex(
        (error: any) =>
          error.itemIndex === itemIndex && error.columnIndex === columnIndex
      );

      if (previousColumnErrorIndex > -1) {
        this.errors.splice(previousColumnErrorIndex, 1, validationResult);
      } else {
        this.errors.push(validationResult);
      }
    }

    if (!isProgrammaticBlur) {
      this.currentlyEditingCell = null;
    }

    if (this._emitChangesDebounced) {
      this._emitChangesDebounced(itemIndex, this.allItems[itemIndex]);
    }
  }

  getErrorsByItemIndex(itemIndex: number): any {
    const errors = this.errors.filter((error) => error.itemIndex === itemIndex);

    if (errors.length > 0) {
      const itemErrors = errors.sort(
        (a, b) => (a.columnIndex as number) - (b.columnIndex as number)
      );

      const validationErrors: IDataGridColumnValidatorResult[] =
        itemErrors.filter(
          (e: IDataGridColumnValidatorResult) =>
            dataGridValidationResultIsError(e) ||
            dataGridValidationResultIsRequired(e)
        );
      const warnings: IDataGridColumnValidatorResult[] = itemErrors.filter(
        (e: IDataGridColumnValidatorResult) =>
          !dataGridValidationResultIsRequired(e) &&
          !dataGridValidationResultIsError(e)
      );

      return validationErrors.concat(warnings);
    }

    return null;
  }

  columnHasError(itemIndex: number, columnIndex: number): boolean {
    const itemErrors = this.getErrorsByItemIndex(itemIndex);

    if (!itemErrors) {
      return false;
    }

    return (
      itemErrors.findIndex((error: any) => error.columnIndex === columnIndex) >
      -1
    );
  }

  resetSelection(): void {
    this.generalSelectionValue = false;
    this.selectedItems = [];
  }

  moveCell(
    itemIndex: number,
    toFocusColumn: number,
    retryNextRow = false
  ): void {
    // Returns boolean saying if item exists and is focussed
    const nextInputName = `input-${itemIndex}-${toFocusColumn}`;
    const currentInputName = `input-${itemIndex}-${this.currentlyEditingCell?.columnIndex}`;
    const nextInput = document.getElementById(nextInputName);
    const currentInput = document.getElementById(currentInputName);
    if (this.currentlyEditingCell) {
      this.onInputBlur(
        null,
        this.currentlyEditingCell.itemIndex,
        this.currentlyEditingCell.columnIndex
      );
    }
    if (nextInput) {
      nextInput.focus();
    } else {
      if (retryNextRow) {
        try {
          const nextIndex = itemIndex - 1;
          this.moveCell(
            nextIndex,
            this._getIndexOfFirstEditableColumnForItem(
              this.shownItems[nextIndex]
            )
          );
        } catch {
          currentInput?.blur();
          return;
        }
      } else {
        this.$emit('endReached');
      }
    }
  }

  onClickSelectionItem(
    itemRef: any,
    colIndex: number,
    itemIndex: number,
    value: any
  ): void {
    const columnRef = this.shownColumns[colIndex];
    const groupedColumns = columnRef.group
      ? this.getColumnGroup(columnRef.group)
      : null;
    itemRef[this.shownColumns[colIndex].valueKey] = value;

    this.$emit('selectedOption', { value, itemRef });

    if (groupedColumns) {
      const valueKeys = groupedColumns
        .flatMap((c: DataGridColumn) => c.valueKey)
        .filter((key: string) => key !== columnRef.valueKey);

      const firstItemWithValues = this.allItems.find((item: any) => {
        let hasValues = false;
        valueKeys.forEach((key: string, index: number) => {
          if ((hasValues && index > 0) || (!hasValues && index === 0)) {
            hasValues = !isUndefined(item[key]);
          }
        });
        return hasValues;
      });
      const firstItemHasErrors = this.getErrorsByItemIndex(itemIndex) !== null;

      if (firstItemWithValues && !firstItemHasErrors) {
        valueKeys.forEach((key: string) => {
          itemRef[key] = firstItemWithValues[key];
        });
      }
    }

    setTimeout(() => {
      this.moveCell(this.getItemIndexFromShownItem(itemIndex), colIndex + 1);
    }, 300);
  }

  setSelectPossiblities(): void {
    this.selectPossibilities = this._getSelectPossiblities();
  }

  private _getSelectPossiblities(): string[] {
    const cell = this.currentlyEditingCell;

    if (!cell) {
      return [];
    }

    const column = this.shownColumns[cell.columnIndex];
    const itemValue = this._getValueFromCell(cell.itemIndex, cell.columnIndex);
    const valueKey = column.valueKey;

    const getValues = () => {
      let values = [];

      if (column.selectOptions && column.selectOptions.length > 0) {
        values = column.selectOptions;
      } else {
        const itemsWithValue = this.allItems.filter(
          (item: any) => item[valueKey]
        ) as string[];
        values = itemsWithValue.map((item: any) => item[valueKey]);
      }

      if (!itemValue || itemValue.length === 1) {
        return values;
      }

      const getOptionValue = (val: string) => {
        return (
          column.selectOptionValueFn ? column.selectOptionValueFn(val) : val
        ).toLowerCase();
      };

      return values.filter((val: string) =>
        getOptionValue(val).includes(itemValue.toLowerCase())
      );
    };

    if (column.showAllSelectOptions) {
      return getValues().filter(distinct);
    }

    return getValues().filter(distinct).slice(0, 4);
  }

  public handleGeneralSelection(): void {
    const items = this.shownItems.filter((item: unknown) =>
      this.gridOptions.isRowSelectableFunc(item)
    );

    items.forEach((item: any) => {
      if (this.isItemSelectable(item)) {
        this.toggleSelection(item, !this.generalSelectionValue);
      }
    });
  }

  isItemSelectable(item: any): boolean {
    return (
      this.gridOptions.isRowEditableFunc(item) &&
      this.gridOptions.isRowSelectableFunc(item)
    );
  }

  toggleSelection(item?: any, forcedValue?: boolean): void {
    if (!item) {
      this.handleGeneralSelection();
      this.$emit('itemSelection', [...this.selectedItems]);
      return;
    }

    if (!this.gridOptions.uniqueValueKey) {
      return;
    }

    const uniqueValue = item[this.gridOptions.uniqueValueKey];
    const selectionIndex = this.selectedItems.indexOf(uniqueValue);

    if (selectionIndex >= 0 || forcedValue === true) {
      this.selectedItems.splice(selectionIndex, 1);
    } else {
      this.selectedItems.push(uniqueValue);
    }

    this.$emit('itemSelection', [...this.selectedItems]);
  }

  isItemSelected(item: any): boolean {
    return this.selectedItems.includes(this._getItemID(item));
  }

  getItemIndexFromShownItem(itemIndex: number) {
    return this._getItemIndexByID(this.shownItems[itemIndex]);
  }

  private _getIndexOfFirstEditableColumnForItem(item: unknown): number {
    return this.shownColumns.findIndex((col: DataGridColumn) => {
      return (
        col.isEditable &&
        !col.isIconColumn &&
        this.gridOptions.isRowEditableFunc(item)
      );
    });
  }

  private _checkIfSelectionBoxesShouldBeVisible(): boolean {
    if (this.shownItems.length === 0) return false;
    return this.shownItems.some((item: unknown) => this.isItemSelectable(item));
  }

  private _getItemID(desiredItem: any): any {
    if (!this.gridOptions.uniqueValueKey) return null;
    return desiredItem[this.gridOptions.uniqueValueKey];
  }

  private _getItemIndexByID(desiredItem: any): any {
    if (!this.gridOptions.uniqueValueKey) return;

    return this.allItems.findIndex(
      (item: any) =>
        item[this.gridOptions.uniqueValueKey as string] ===
        desiredItem[this.gridOptions.uniqueValueKey as string]
    );
  }

  private _getValueFromCell(itemIndex: number, columnIndex: number): any {
    const column = this.shownColumns[columnIndex];
    const item: any = this.allItems[itemIndex];

    return item[column.valueKey];
  }

  private _emitChanges(itemIndex: number, item: any): void {
    this.$emit('updatedItem', {
      itemIndex,
      item,
    });
  }

  private getColumnGroup(columnGroupName: string): DataGridColumn[] | null {
    if (!columnGroupName) return null;
    return this.shownColumns.filter(
      (c: DataGridColumn) => (c.group ?? undefined) === columnGroupName
    );
  }
}
